import { prioritizeAndMapDropdownItems } from '@approvalmax/ui';
import { selectors } from 'modules/common';
import { AsyncDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRuleTypeByEditMode } from 'pages/requestForm/utils/helpers';
import { FC, useCallback } from 'react';
import { useLazyGetNetSuiteTaxCodes } from 'shared/data/webApp/v1';
import { ApiTypes } from 'shared/data/webApp/v2';
import { isOcrEnabledForTemplate } from 'shared/helpers';
import { useCompanyBetaFeatures } from 'shared/hooks';

import { useGetRecognizedOcrAttachment } from '../../../../hooks/useGetRecognizedOcrAttachment';
import { NetSuiteTaxCodesDataProviderProps } from './NetSuiteTaxCodesDataProvider.types';

const NetSuiteTaxCodesDataProvider: FC<NetSuiteTaxCodesDataProviderProps> = (props) => {
    const { children, lineIndex } = props;

    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);

    const { id: requestId, companyId } = request;

    const company = useSelector((state) => selectors.company.getCompanyById(state, companyId));

    const [getNetSuiteTaxCodes] = useLazyGetNetSuiteTaxCodes();

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const shouldUseOcr = ocrAttachment && isOcrEnabledForTemplate(company, request.integrationCode);

    const { data: ocrData, refetch: refetchOcrData } = useOcrData(request, ocrAttachment?.id);

    const { isNetSuiteShowAllTaxes } = useCompanyBetaFeatures(companyId);

    const onLoad = useCallback(
        async (filterText: string | null, startFrom: number, count: number) => {
            const response = await getNetSuiteTaxCodes({
                params: {
                    query: {
                        requestId,
                        ruleType,
                        query: filterText,
                        startFrom,
                        rowNum: isNetSuiteShowAllTaxes ? 20000 : count,
                        companyId,
                    },
                },
            });

            const parsedTaxCodes = response.values.map((taxItem) => ({ ...taxItem, text: taxItem.name }));

            if (shouldUseOcr && lineIndex !== undefined) {
                const document = (ocrData || (await refetchOcrData()).data)
                    ?.document as ApiTypes['OcrContractsModelsNetSuiteRecognizedBillAnswerCamelCased'];

                return prioritizeAndMapDropdownItems(parsedTaxCodes, document?.lineItems?.[lineIndex]?.taxCodeIds);
            }

            return parsedTaxCodes;
        },
        [
            getNetSuiteTaxCodes,
            requestId,
            ruleType,
            isNetSuiteShowAllTaxes,
            companyId,
            shouldUseOcr,
            ocrData,
            refetchOcrData,
            lineIndex,
        ]
    );

    return (
        <AsyncDataProvider
            cacheStorageId={`requestOptions.netSuiteTaxCodes.${requestId}`}
            pageSize={100}
            onLoad={onLoad}
        >
            {children}
        </AsyncDataProvider>
    );
};

export default NetSuiteTaxCodesDataProvider;
